import React from 'react';
import {
  BriefcaseBusinessIcon,
  CircleCheckIcons,
  EyeIcons,
  FilePlusIcons,
  MapPinIcons,
  PhoneIcons,
} from '../../../icons/icons';
import { NameEmailAvatar } from '../../../ui';
import { CandidateType } from '../constants/mockData';

interface CandidateCardProps {
  candidate: CandidateType;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  isSelected?: boolean;
  onSelect?: () => void;
  onViewProfile?: (candidate: CandidateType) => void;
  onInvite?: (candidate: CandidateType) => void;
}

/** Deterministic pastel background colour from a string */
const avatarColor = (name: string) => {
  const colors = [
    'bg-indigo-100 text-indigo-700',
    'bg-violet-100 text-violet-700',
    'bg-sky-100 text-sky-700',
    'bg-emerald-100 text-emerald-700',
    'bg-rose-100 text-rose-700',
    'bg-amber-100 text-amber-700',
    'bg-teal-100 text-teal-700',
    'bg-pink-100 text-pink-700',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++)
    h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return colors[Math.abs(h) % colors.length];
};

const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');

export const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  getStatusColor,
  getStatusText,
  getMatchColor,
  isSelected = false,
  onSelect,
  onViewProfile,
  onInvite,
}) => {
  const scorePercent = Math.round(candidate.score * 100);
  const expYears = Math.floor(candidate.experience);
  const latestRole = candidate.work_experience?.[0]?.job_title ?? null;
  const avatarColorClass = avatarColor(candidate.name);

  return (
    <div
        onClick={() => onViewProfile?.(candidate)}
        className={`group relative flex flex-col bg-white border transition-all duration-200 cursor-pointer rounded-2xl overflow-hidden ${
          isSelected
            ? 'border-indigo-400 ring-2 ring-indigo-100 shadow-md shadow-indigo-100/60'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-100/80'
        }`}
    >
      { /* Top bar: checkbox + score */ }
      <div className='flex items-center justify-between px-4 pt-3 pb-0'>
        { /* Checkbox */ }
        <div
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.();
            }}
            className={`w-4 h-4 rounded border-2 transition-all flex items-center justify-center flex-shrink-0 cursor-pointer ${
              isSelected
                ? 'bg-indigo-600 border-indigo-600'
                : 'border-slate-300 bg-white group-hover:border-indigo-400'
            }`}
        >
          { isSelected && (
            <svg
                viewBox='0 0 24 24'
                className='w-2.5 h-2.5 text-white'
                fill='none'
                stroke='currentColor'
                strokeWidth='3.5'
                strokeLinecap='round'
                strokeLinejoin='round'
            >
              <polyline points='20 6 9 17 4 12' />
            </svg>
          ) }
        </div>

        { /* Match score */ }
        <div
            className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full text-white ${getMatchColor(
              scorePercent,
            )}`}
        >
          { scorePercent }%
        </div>
      </div>

      <div className='px-4 pb-4 pt-3 flex flex-col flex-1 gap-3'>
        { /* Avatar + Name + Location + Role */ }
        <div className=' gap-3'>
          <div className='relative flex-shrink-0'>
            <NameEmailAvatar
                avatar={candidate.avatar}
                name={candidate.name}
                email={candidate.email}
            />
          </div>
          <div className='min-w-0 flex-1'>
            { latestRole && (
              <p className='text-[11px] text-slate-500 font-medium truncate'>
                { latestRole }
              </p>
            ) }
            <div className='flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 font-medium'>
              { candidate.location && (
                <span className='flex items-center gap-0.5'>
                  <MapPinIcons className='w-3 h-3' />
                  { candidate.location }
                </span>
              ) }
              <span className='flex items-center gap-0.5'>
                <BriefcaseBusinessIcon className='w-3 h-3' />
                { expYears }y exp
              </span>
              <span
                  className={`ml-auto inline-flex items-center gap-1 text-[9px] font-bold uppercase ${
                    candidate.status === 'Open to Opportunities' ||
                  candidate.status === 'Actively Looking'
                      ? 'text-emerald-600'
                      : 'text-slate-400'
                  }`}
              >
                <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      candidate.status === 'Open to Opportunities' ||
                    candidate.status === 'Actively Looking'
                        ? 'bg-emerald-500'
                        : 'bg-slate-300'
                    }`}
                />
                { getStatusText(candidate.status) }
              </span>
            </div>
          </div>
        </div>

        { /* Skills */ }
        { candidate.skills.length > 0 && (
          <div className='flex flex-wrap gap-1'>
            { candidate.skills.slice(0, 5).map((skill, i) => (
              <span
                  key={i}
                  className='px-2 py-0.5 bg-slate-50 text-slate-600 text-[10px] font-medium rounded-md border border-slate-200'
              >
                { skill }
              </span>
            )) }
            { candidate.skills.length > 5 && (
              <span className='px-2 py-0.5 text-slate-400 text-[10px] font-medium'>
                +{ candidate.skills.length - 5 } more
              </span>
            ) }
          </div>
        ) }

        { /* Invite Lists */ }
        { candidate.inviteLists && candidate.inviteLists.length > 0 && (
          <div className='px-2.5 py-2 bg-indigo-50/60 rounded-lg border border-indigo-100'>
            <div className='flex items-center gap-1 mb-1'>
              <CircleCheckIcons className='w-3 h-3 text-indigo-500' />
              <span className='text-[9px] font-bold text-indigo-500 uppercase tracking-wide'>
                Active in lists
              </span>
            </div>
            <div className='flex flex-wrap gap-1'>
              { candidate.inviteLists.slice(0, 2).map((list, idx) => (
                <span
                    key={idx}
                    className='text-[10px] text-indigo-700 bg-white px-1.5 py-0.5 rounded border border-indigo-100 truncate max-w-full'
                >
                  { list }
                </span>
              )) }
            </div>
          </div>
        ) }

        { /* Action Bar */ }
        <div className='pt-3 mt-auto border-t border-slate-100 flex items-center justify-between gap-2'>
          <div className='flex items-center gap-0.5'>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewProfile?.(candidate);
                }}
                className='p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-95'
                title='View Profile'
            >
              <EyeIcons className='w-4 h-4' />
            </button>
            <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className='p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all active:scale-95'
                title='Contact'
            >
              <PhoneIcons className='w-4 h-4' />
            </button>
          </div>

          <button
              onClick={(e) => {
                e.stopPropagation();
                onInvite?.(candidate);
              }}
              className='px-3.5 py-1.5 bg-slate-900 text-white text-[11px] font-bold rounded-lg hover:bg-indigo-600 transition-all shadow-sm active:scale-[0.98] flex items-center gap-1.5'
          >
            <FilePlusIcons className='w-3 h-3' />
            Invite
          </button>
        </div>
      </div>
    </div>
  );
};
